type IdleCallbackHandle = number;
type IdleCallback = (deadline: { timeRemaining: () => number }) => void;

const FALLBACK_IDLE_DELAY_MS = 180;

export function runWhenPageReady(callback: () => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  if (document.readyState === 'complete') {
    callback();
    return () => {};
  }

  const handleLoad = () => {
    callback();
  };

  window.addEventListener('load', handleLoad, { once: true });
  return () => {
    window.removeEventListener('load', handleLoad);
  };
}

export function runWhenPageReadyAndIdle(callback: () => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let cancelled = false;
  let idleHandle: IdleCallbackHandle | null = null;
  const idleWindow = window as Window & {
    requestIdleCallback?: (cb: IdleCallback) => IdleCallbackHandle;
    cancelIdleCallback?: (id: IdleCallbackHandle) => void;
  };

  const cancelIdle = () => {
    if (idleHandle === null) return;
    if (typeof idleWindow.cancelIdleCallback === 'function') {
      idleWindow.cancelIdleCallback(idleHandle);
      return;
    }
    window.clearTimeout(idleHandle);
  };

  const queueIdle = () => {
    if (cancelled) return;
    if (typeof idleWindow.requestIdleCallback === 'function') {
      idleHandle = idleWindow.requestIdleCallback(() => {
        if (!cancelled) {
          callback();
        }
      });
      return;
    }

    idleHandle = window.setTimeout(() => {
      if (!cancelled) {
        callback();
      }
    }, FALLBACK_IDLE_DELAY_MS);
  };

  const stopWaitingForReady = runWhenPageReady(queueIdle);
  return () => {
    cancelled = true;
    stopWaitingForReady();
    cancelIdle();
  };
}
